import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, X, Stack, ArrowClockwise } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * BulkSendInvoicesBar — top-of-page banner that surfaces on the Invoices page
 * whenever the pilot has one or more draft invoices with a customer_email on
 * file. One click emails them all in a single batch (branded PDF + optional
 * load paperwork auto-attach). Auto-hides when there's nothing to send.
 */
export default function BulkSendInvoicesBar({ onSent }) {
  const [stats, setStats] = useState({ draft_ready: 0, draft_no_email: 0 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await client.get("/invoices");
      const drafts = data.filter((i) => i.status === "draft");
      const ready = drafts.filter((i) => (i.customer_email || "").trim());
      setStats({
        draft_ready: ready.length,
        draft_no_email: drafts.length - ready.length,
      });
    } catch {
      /* silent — bar just hides */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || stats.draft_ready === 0) return null;

  return (
    <>
      <div
        className="rounded-2xl border border-[#FF5A1F]/25 bg-[#FF5A1F]/[0.06] p-4 flex items-center justify-between gap-4 flex-wrap"
        data-testid="bulk-send-bar"
      >
        <div className="flex items-start gap-3">
          <Stack size={20} weight="fill" className="text-[#FF5A1F] shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-white">
              {stats.draft_ready} draft invoice{stats.draft_ready !== 1 ? "s" : ""} ready to send
            </div>
            <div className="text-[12px] text-white/60 mt-0.5">
              Email every draft with a customer email in one click — branded PDF + any load paperwork
              auto-attached.
              {stats.draft_no_email > 0 && (
                <span className="text-amber-300 ml-1">
                  ({stats.draft_no_email} skipped — no email on file)
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] px-4 py-2 text-xs font-semibold text-white hover:bg-[#FF5A1F]/90"
          data-testid="bulk-send-open"
        >
          <PaperPlaneTilt size={13} weight="fill" />
          Send all drafts
        </button>
      </div>
      {modalOpen && (
        <BulkSendConfirmModal
          readyCount={stats.draft_ready}
          skippedCount={stats.draft_no_email}
          onClose={() => setModalOpen(false)}
          onSent={() => { load(); onSent && onSent(); }}
        />
      )}
    </>
  );
}

function BulkSendConfirmModal({ readyCount, skippedCount, onClose, onSent }) {
  const [attachDocs, setAttachDocs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/invoices/bulk-send", {
        status: "draft",
        attach_load_docs: attachDocs,
      });
      setResult(data);
      if (data.sent > 0) {
        toast.success(`Sent ${data.sent} invoice${data.sent !== 1 ? "s" : ""}`);
        onSent && onSent();
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} send${data.failed !== 1 ? "s" : ""} failed`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="bulk-send-modal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-send-modal"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FF5A1F]">
              Bulk send drafts
            </div>
            <h2 className="mt-1 text-xl font-bold text-white">
              Email {readyCount} draft invoice{readyCount !== 1 ? "s" : ""}?
            </h2>
            <div className="mt-1 text-xs text-white/50">
              Each customer receives the branded PDF via Resend.
              {skippedCount > 0 && (
                <span className="block mt-1 text-amber-300">
                  {skippedCount} draft{skippedCount !== 1 ? "s" : ""} without a customer email will be skipped.
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-white/50 hover:bg-white/5 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <div className="mt-5 space-y-3">
            <label className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 cursor-pointer hover:bg-white/[0.05]">
              <input
                type="checkbox"
                checked={attachDocs}
                onChange={(e) => setAttachDocs(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#FF5A1F]"
                data-testid="bulk-send-attach-toggle"
              />
              <div className="text-xs text-white/80">
                <div className="font-medium">Auto-attach load paperwork</div>
                <div className="text-white/50">
                  Include Rate Con / BOL / POD for every invoice with a matching load reference.
                </div>
              </div>
            </label>
            <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs text-white/50">
              Drafts that succeed will auto-flip to <b>sent</b>. Failures leave the draft untouched
              so you can retry.
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button" onClick={onClose} disabled={busy}
                className="rounded-lg border border-white/10 bg-transparent px-4 py-2 text-sm font-medium text-white/70 hover:bg-white/5"
                data-testid="bulk-send-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submit} disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90 disabled:opacity-50"
                data-testid="bulk-send-submit"
              >
                {busy ? <ArrowClockwise size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
                {busy ? "Sending…" : `Send ${readyCount}`}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-3" data-testid="bulk-send-result">
            <div className="grid grid-cols-3 gap-2 text-center">
              <ResultCell label="Sent" value={result.sent} tone="emerald" />
              <ResultCell label="Failed" value={result.failed} tone="rose" />
              <ResultCell label="Skipped" value={result.skipped} tone="neutral" />
            </div>
            <div className="max-h-56 overflow-auto space-y-1">
              {result.results.slice(0, 25).map((r, i) => (
                <div
                  key={i}
                  className="text-[11px] px-2 py-1.5 rounded bg-white/[0.02] flex items-center justify-between gap-2"
                >
                  <span className="font-mono text-neutral-300 truncate">#{r.invoice_number}</span>
                  <span className={`shrink-0 uppercase tracking-widest ${
                    r.status === "sent" ? "text-emerald-300" :
                    r.status === "skipped" ? "text-amber-300" : "text-rose-300"
                  }`}>
                    {r.status}
                    {r.reason && r.status !== "sent" ? ` · ${r.reason}` : ""}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={onClose}
                className="rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90"
                data-testid="bulk-send-done"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultCell({ label, value, tone }) {
  const toneMap = {
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
    rose: "text-rose-300 border-rose-500/30 bg-rose-500/10",
    neutral: "text-neutral-300 border-white/10 bg-white/5",
  };
  return (
    <div className={`rounded-lg border p-3 ${toneMap[tone]}`}>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-widest mt-0.5 opacity-80">{label}</div>
    </div>
  );
}
